'use client';

import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import type { SwiperOptions } from 'swiper/types';
import type { Swiper as SwiperCore } from 'swiper';
import { type PromotionTallBannerSwipeItemType, type PromotionTallBannerSwipeWidgetData } from '@weberus/shared-types';
import { type FC, forwardRef } from 'react';
import { useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useIsMounted } from '@weberus/ui/hooks/use-is-mounted';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import { chunk } from 'lodash-es';
import { YaTrackerImpression } from '@weberus/client-tracker';
import { waitUntilClickTimeout } from '@weberus/utils';
import { FloatingButton, PageIndicator, Thumbnail } from '@yanolja-rnd/nol-design-system';
import { IcArrowbackFill, IcArrowforwardFill } from '@yanolja-rnd/nol-icons';
import { clientLogProtobuf } from '@weberus/client-tracker/utils';
import { cn } from '@/app/_shared/utils/tw-cn';
import { PromotionTallBannerSwipeItem } from '@/app/_components/widgets/promotion-tall-banner-swipe/promotion-tall-banner-swipe-item';
import { deviceAtom, pcWidthAtom } from '@/app/_shared/store/device';
import { navigateTo } from '@/app/_shared/utils/action-navigator';
import { PromotionTallBannerSwipeModal } from '@/app/_components/widgets/promotion-tall-banner-swipe/promotion-tall-banner-swipe-modal';
import { HomePageLog } from '@/app/_shared/protobuf-log/clientlogs';
import { PromoBannerRoundedListPageLog } from '@/app/_shared/protobuf-log/clientlogs/misc-common-txtpb';

export interface PromotionTallBannerSwipeProps {
  data: PromotionTallBannerSwipeWidgetData;
  pageNameLog: HomeLogPageNameValue;
}
const MAX_SLIDES_PER_VIEW = {
  PC: 4, // PC에서 한 번에 보여줄 최대 슬라이드 수
  MOBILE: 3,
} as const;

/*
 * 네비게이션
 */
const TallBannerNavigationButton = forwardRef<
  HTMLDivElement,
  {
    type: 'prev' | 'next';
    isVisible: boolean;
  }
>(({ type, isVisible }, ref) => {
  const isPrev = type === 'prev';

  return (
    <div
      ref={ref}
      className={cn(
        'absolute top-1/2 z-[1] hidden -translate-y-1/2 pc:block',
        isPrev ? 'left-0 -translate-x-2/4' : 'right-0 translate-x-2/4',
        { 'pc:hidden': !isVisible },
      )}>
      <FloatingButton icon={isPrev ? <IcArrowbackFill /> : <IcArrowforwardFill />} />
    </div>
  );
});
TallBannerNavigationButton.displayName = 'TallBannerNavigationButton';

/*
 * 페이지 인디케이터
 */
const TallBannerPageIndicator: FC<{
  variant: 'mobile' | 'pc';
  currentPage: number;
  isPlaying: boolean;
  totalPages: number;
  isVisible: boolean;
  onClickAll: () => void;
  onPlayChange: () => void;
}> = ({ variant, currentPage, isPlaying, totalPages, isVisible, onClickAll, onPlayChange }) => {
  const isMobile = variant === 'mobile';

  return (
    <div
      className={cn(
        'mt-2 flex justify-center',
        // 모바일/PC 표시 설정
        isMobile ? 'pc:mt-16 pc:hidden' : 'hidden pc:mt-16 pc:flex',
        // 표시 여부에 따라 해당 디바이스에서 숨김 처리
        { [isMobile ? 'hidden' : 'pc:hidden']: !isVisible },
      )}>
      <PageIndicator
        currentPage={currentPage}
        isPlaying={isPlaying}
        totalPages={totalPages}
        onClickAll={onClickAll}
        onPlayChange={onPlayChange}
      />
    </div>
  );
};

export const PromotionTallBannerSwipe: FC<PromotionTallBannerSwipeProps> = ({ data }) => {
  const { items } = data;
  const { isPcWidth } = useAtomValue(pcWidthAtom);
  const { deviceType } = useAtomValue(deviceAtom);
  const isMounted = useIsMounted();

  // Swiper
  const swiperRef = useRef<SwiperCore | null>(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  // 상태 관리
  const [currentPage, setCurrentPage] = useState(1);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isSeeAllModalOpen, setIsSeeAllModalOpen] = useState(false);

  // 네비게이션 및 인디케이터 표시 여부
  const showNav = items.length > MAX_SLIDES_PER_VIEW.PC;
  const showMobileIndicator = items.length > MAX_SLIDES_PER_VIEW.MOBILE;
  const showPcIndicator = items.length > MAX_SLIDES_PER_VIEW.PC;
  const pcTotalPages = Math.ceil(items.length / MAX_SLIDES_PER_VIEW.PC);

  const swiperConfig: SwiperOptions = useMemo(() => {
    return {
      spaceBetween: isPcWidth ? 20 : 0,
      slidesOffsetBefore: isPcWidth ? 0 : 20,
      slidesOffsetAfter: 0,
      loop: true,
      loopAddBlankSlides: true, // loop 모드에서는 slidesPerView + slidesPerGroup 보다 많은 슬라이드가 필요하므로 빈 슬라이드를 추가
      autoplay: { delay: data.rollingInterval, disableOnInteraction: false },
      slidesPerView: 'auto' as const,
      centeredSlidesBounds: !isPcWidth,
    };
  }, [isPcWidth]);

  const handleSwiper = (_swiper: SwiperCore) => {
    swiperRef.current = _swiper;

    if (prevRef.current && nextRef.current) {
      _swiper.navigation.init();
      _swiper.navigation.update();
    }
  };

  /* 시나리오에 맞춘 item 구조화*/
  const shouldCenterItems = isPcWidth && items.length < MAX_SLIDES_PER_VIEW.PC; // 아이템이 4개 미만일 때 중앙 정렬
  const groups = useMemo(
    () => chunk(items, isPcWidth ? MAX_SLIDES_PER_VIEW.PC : 1), // PC에서는 4개씩, 모바일에서는 1개씩 그룹화
    [items, isPcWidth],
  );

  /**
   * 자동 재생 토글 함수
   */
  const toggleAutoplay = () => {
    swiperRef.current?.autoplay.running ? swiperRef.current.autoplay.stop() : swiperRef.current?.autoplay.start();
  };

  const handleItemClick = async (e: React.MouseEvent, item: PromotionTallBannerSwipeItemType) => {
    e.preventDefault();
    await waitUntilClickTimeout(HomePageLog.PromotionTallBannerSwipeId.click({ logMeta: item.serverLogMeta }));
    item.action && navigateTo(item.action);
  };

  const handleItemImpression = (item: PromotionTallBannerSwipeItemType) => {
    clientLogProtobuf(HomePageLog.PromotionTallBannerSwipeId.impression, { logMeta: item.serverLogMeta })();
  };

  const handleOpenModal = () => {
    setIsSeeAllModalOpen(true);
    clientLogProtobuf(HomePageLog.PromotionTallBannerSwipeButtonSeeAll.click, {
      logMeta: data.header.more?.serverLogMeta,
    })();
    PromoBannerRoundedListPageLog.PromotionTallBannerSwipe.view();
  };

  return (
    <div className="my-16 w-full pc:my-40 pc:w-auto pc:max-w-pc-size">
      <div className="relative">
        {isMounted ? (
          <div className="relative">
            <Swiper
              {...swiperConfig}
              modules={[Autoplay, Navigation]}
              navigation={{
                prevEl: prevRef.current,
                nextEl: nextRef.current,
              }}
              onAutoplayStart={() => {
                setIsPlaying(true);
              }}
              onAutoplayStop={() => {
                setIsPlaying(false);
              }}
              onRealIndexChange={(_swiper) => {
                setCurrentPage(_swiper.realIndex + 1);
              }}
              onSwiper={(_swiper) => {
                handleSwiper(_swiper);
              }}>
              {isPcWidth ? (
                <>
                  {groups.map((group, groupIndex) => (
                    <SwiperSlide key={groupIndex} className="flex">
                      <div className={cn(shouldCenterItems ? 'flex justify-center gap-20' : 'flex w-full gap-20')}>
                        {group.map((item, itemIdx) => (
                          <YaTrackerImpression key={itemIdx} onImpressed={() => handleItemImpression(item)}>
                            <PromotionTallBannerSwipeItem
                              {...item}
                              width={305}
                              height={407}
                              onClick={(e) => handleItemClick(e, item)}
                            />
                          </YaTrackerImpression>
                        ))}
                      </div>
                    </SwiperSlide>
                  ))}
                </>
              ) : (
                items.map((item, groupIndex) => (
                  <SwiperSlide key={groupIndex} className="mx-4 !w-fit last:mr-0 pc:mr-20">
                    <YaTrackerImpression key={groupIndex} onImpressed={() => handleItemImpression(item)}>
                      <PromotionTallBannerSwipeItem
                        {...item}
                        width={195}
                        height={260}
                        onClick={(e) => handleItemClick(e, item)}
                      />
                    </YaTrackerImpression>
                  </SwiperSlide>
                ))
              )}
            </Swiper>
            {/* 네비게이션 버튼 */}
            <TallBannerNavigationButton ref={prevRef} isVisible={showNav} type="prev" />
            <TallBannerNavigationButton ref={nextRef} isVisible={showNav} type="next" />
          </div>
        ) : (
          /* Fallback */
          <div className="flex overflow-hidden !pl-24 pc:!px-0">
            <div className="flex w-full gap-8 pc:gap-20">
              {items.map((item, index) => (
                <Thumbnail
                  key={index}
                  image={item.image.url}
                  className="h-[260px] w-[195px] flex-shrink-0 rounded-12 pc:h-[407px] pc:w-[305px]"
                  imgClassName="object-top"
                  ratio="3_4"
                  shape="roundedRectangle_12"
                  onClick={(e) => handleItemClick(e, item)}
                />
              ))}
            </div>
          </div>
        )}
        {/* 페이지 인디케이터 */}
        <TallBannerPageIndicator
          currentPage={currentPage}
          isPlaying={isPlaying}
          isVisible={showMobileIndicator}
          totalPages={items.length}
          variant="mobile"
          onClickAll={handleOpenModal}
          onPlayChange={toggleAutoplay}
        />

        <TallBannerPageIndicator
          currentPage={currentPage}
          isPlaying={isPlaying}
          isVisible={showPcIndicator}
          totalPages={pcTotalPages}
          variant="pc"
          onClickAll={handleOpenModal}
          onPlayChange={toggleAutoplay}
        />
      </div>
      <PromotionTallBannerSwipeModal
        isOpen={isSeeAllModalOpen}
        data={data}
        deviceType={deviceType}
        onClose={() => {
          setIsSeeAllModalOpen(false);
        }}
      />
    </div>
  );
};
